import { Icon } from '@components/primitives/Icon'
import { Text } from '@components/primitives/Text'
import { Tooltip } from '@components/primitives/Tooltip'
import { classMerge } from '@components/utilities/classMerge'
import { faBullseyeArrow } from '@fortawesome/pro-regular-svg-icons/faBullseyeArrow'
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons/faCircleInfo'
import { type OutcomeNarrow } from '@modules/trials/helpers/parseMeasurementsFromTrial'
import sortBy from 'lodash/sortBy'
import { useState } from 'react'

export const MeasurementsCard = ({
  allGroupsExpanded,
  items,
  timeline,
  timelineInfo,
}: {
  allGroupsExpanded: boolean
  items: OutcomeNarrow[]
  timeline: string
  timelineInfo?: string
}) => {
  const [isManuallyExpanded, setIsExpanded] = useState(false)
  const shouldShowExtraItems = allGroupsExpanded || isManuallyExpanded

  const sortedMeasurements = sortBy(items, 'measure')
  const firstThreeItems = sortedMeasurements.splice(0, 3)
  const remainingItems = sortedMeasurements
  const hasHiddenItems = remainingItems.length > 0

  return (
    <div
      className={classMerge(
        'lg:rounded-2xl',
        'rounded-xl',
        'border',
        'border-neutral200',
        'lg:p-6',
        'p-4',
        'w-[90%]',
        'lg:w-[45%]',
        'flex-none',
      )}
    >
      <div className='flex'>
        <Text className='mb-2' styleName='p-strong' value={timeline} />
        {timelineInfo && (
          <Tooltip
            description={timelineInfo}
            label={<Icon className='ml-2' icon={faCircleInfo} />}
            labelClassName='text-neutral600'
          />
        )}
      </div>
      <div className='flex flex-col space-y-2'>
        {firstThreeItems.map((measurement) => (
          <MeasurementItem
            key={`${timeline} - ${measurement.measure}`}
            measurement={measurement}
          />
        ))}
        {shouldShowExtraItems &&
          remainingItems.map((measurement) => (
            <MeasurementItem
              key={`${timeline} - ${measurement.measure}`}
              measurement={measurement}
            />
          ))}
        {!shouldShowExtraItems && hasHiddenItems && (
          <Text
            className='ml-6 underline'
            onClick={() => setIsExpanded(true)}
            value={`+${remainingItems.length} more`}
          />
        )}
      </div>
    </div>
  )
}

const MeasurementItem = ({ measurement }: { measurement: OutcomeNarrow }) => (
  <div className='flex items-baseline'>
    <div className='pt-1'>
      <Icon className='mr-2 text-success500' icon={faBullseyeArrow} />
    </div>

    <Text
      className='w-full text-neutral600 no-underline'
      styleName='p-small'
      value={measurement.preferredTerm ?? measurement.measure}
    />
    {measurement.description && (
      <Tooltip
        description={measurement.description}
        label={<Icon className='ml-2 cursor-pointer' icon={faCircleInfo} />}
        labelClassName='text-neutral600'
      />
    )}
  </div>
)
